import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { get, post } from '/api.js';

/**
 * What the QR on the wall opens: join the line, as yourself.
 *
 * Joining at the tablet is only ever as a guest, and it has to be — the
 * tablet cannot know who is standing at it. A phone knows exactly who it is.
 * So the code on the wall carries the pub and the phone carries the person,
 * and neither has to know anything about the other.
 *
 * Being called to the table is the reason it matters: a guest can only be
 * shouted at, whereas somebody who joined as themselves gets a push while
 * they are at the bar.
 */
export function JoinQueue({ token, me, onGo, onDone }) {
  const [where, setWhere] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const at = `/api/queue/${encodeURIComponent(token)}`;

  const load = () => get(at)
    .then(setWhere)
    .catch((e) => setError(e.detail || e.message));

  useEffect(() => { load(); }, [token]);

  if (error && !where) {
    return html`
      <div class="screen" data-testid="joinq-screen">
        <h2>The queue</h2>
        <p class="error" data-testid="joinq-error">${error}</p>
        <p class="sub">
          ${/* Expired and forged are deliberately indistinguishable to the
                server, so the words have to cover both without guessing. */''}
          That code is out of date, or was never one of ours. The screen in
          the pub shows a fresh one every few minutes — scan that.
        </p>
        <button class="secondary" onClick=${onDone}>Back</button>
      </div>`;
  }
  if (!where) return html`<p class="sub">Loading…</p>`;

  const join = async () => {
    setBusy(true);
    setError('');
    try {
      await post(at, {});
      await load();
    } catch (e) {
      setError(e.detail || e.message);
    } finally { setBusy(false); }
  };

  const place = where.mine
    ? (where.entries || []).findIndex((e) => e.entry_id === where.mine) + 1
    : 0;

  return html`
    <div class="screen" data-testid="joinq-screen">
      <div class="rowbetween">
        <h2 data-testid="joinq-venue">${where.name}</h2>
        <button class="secondary" style="width:auto"
                data-testid="joinq-back" onClick=${onDone}>Back</button>
      </div>

      <p class="sub" data-testid="joinq-waiting">
        ${where.waiting === 0
          ? 'Nobody waiting — the table is yours.'
          : `${where.waiting} waiting for the table.`}
      </p>

      ${where.mine
        ? html`
          <div class="uptonow" data-testid="joinq-mine">
            <div class="name">You are in the line</div>
            <div class="figure">${place > 0 ? `#${place}` : 'In'}</div>
            <p class="sub">
              ${(where.entries || []).find((e) => e.entry_id === where.mine
                                            && e.table)
                ? `You are up — ${(where.entries.find(
                    (e) => e.entry_id === where.mine) || {}).table}. Tap to
                   confirm at the screen in the pub.`
                : 'Stay where you are. Your phone will buzz when the table is yours, and you tap to confirm at the screen in the pub.'}
            </p>
          </div>`
        : html`
          <button class="felt" disabled=${busy} data-testid="joinq-join"
                  onClick=${join}>
            ${busy ? 'Joining…' : 'Join the queue'}
          </button>`}

      ${(where.entries || []).length > 0 && html`
        <ol class="line" data-testid="joinq-line">
          ${where.entries.map((e, i) => html`
            <li key=${e.entry_id}
                class=${`seat ${e.state} ${e.user_id === me ? 'mine' : ''}`}>
              <span class="mono">${i + 1}</span>
              <span class="who">${e.name}</span>
              <span class="mono tag">
                ${e.state === 'here' ? 'at the table'
                  : e.state === 'called' ? 'called' : ''}
              </span>
            </li>`)}
        </ol>`}

      ${error && html`<p class="error" data-testid="joinq-join-error">
                        ${error}
                      </p>`}
    </div>`;
}
